/**
 * Admin page for managing background jobs
 */

import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Pause,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Clock,
  RotateCcw,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Loader2,
} from 'lucide-react'
import { cn } from '../../lib/utils'

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8899'

interface JobProgress {
  current: number
  total: number
  percentage: number
  message: string | null
}

interface JobInputParams {
  reference_year?: number
  edition_name?: string
  edition_mode?: string
  original_job_id?: string
  data_source_id?: number
  // CSV Import specific
  target_table?: string
  entity_level?: string
  file_path?: string
  // Regionalstatistik specific
  display_name?: string
  table_code?: string
  statistics_label?: string
  time_label?: string
  time_value?: string
  force_update?: boolean
}

interface Job {
  job_id: string
  job_type: string
  status: string
  progress: JobProgress
  created_at: string | null
  started_at: string | null
  completed_at: string | null
  duration_seconds: number | null
  can_rollback: boolean
  error_message: string | null
  result: Record<string, unknown> | null
  input_params: JobInputParams | null
}

interface JobListResponse {
  total: number
  jobs: Job[]
}

interface JobStats {
  status_counts: Record<string, number>
  type_counts: Record<string, number>
  total_jobs: number
  recent_jobs: Job[]
}

// Status badge colors and icons
const statusConfig: Record<string, { color: string; icon: React.ReactNode; label: string }> = {
  pending: { color: 'bg-gray-100 text-gray-700', icon: <Clock className="w-4 h-4" />, label: 'Wartend' },
  queued: { color: 'bg-blue-100 text-blue-700', icon: <Clock className="w-4 h-4" />, label: 'In Warteschlange' },
  running: { color: 'bg-yellow-100 text-yellow-700', icon: <Loader2 className="w-4 h-4 animate-spin" />, label: 'Läuft' },
  completed: { color: 'bg-green-100 text-green-700', icon: <CheckCircle2 className="w-4 h-4" />, label: 'Abgeschlossen' },
  failed: { color: 'bg-red-100 text-red-700', icon: <XCircle className="w-4 h-4" />, label: 'Fehlgeschlagen' },
  cancelled: { color: 'bg-gray-100 text-gray-700', icon: <Pause className="w-4 h-4" />, label: 'Abgebrochen' },
  rolled_back: { color: 'bg-purple-100 text-purple-700', icon: <RotateCcw className="w-4 h-4" />, label: 'Rückgängig' },
}

// Job type labels
const jobTypeLabels: Record<string, string> = {
  gv100_import: 'GV100 Import',
  gv100_nad_import: 'GV100 NAD Import',
  csv_import: 'CSV Import',
  rs_import: 'Regionalstatistik',
  rollback: 'Rollback',
  maintenance: 'Wartung',
}

// Target table display names
const targetTableLabels: Record<string, string> = {
  indicator_values: 'Indikatoren',
}

// Entity level display names
const entityLevelLabels: Record<string, string> = {
  municipality: 'Gemeinden',
  county: 'Kreise',
  state: 'Bundesländer',
}

// Helper to format job subtitle based on input_params
function getJobSubtitle(job: Job): string | null {
  const params = job.input_params
  if (!params) return null

  // Regionalstatistik imports - use display_name if available
  if (job.job_type === 'rs_import') {
    if (params.display_name) {
      return params.display_name
    }
    const parts: string[] = []
    if (params.statistics_label) {
      parts.push(params.statistics_label)
    } else if (params.table_code) {
      parts.push(`Tabelle ${params.table_code}`)
    }
    if (params.time_label && params.time_value) {
      parts.push(`${params.time_label}: ${params.time_value}`)
    } else if (params.reference_year) {
      parts.push(String(params.reference_year))
    }
    if (params.force_update) {
      parts.push('(Update)')
    }
    return parts.length > 0 ? parts.join(', ') : null
  }

  // CSV imports
  if (job.job_type === 'csv_import') {
    const parts: string[] = []
    if (params.target_table) {
      parts.push(targetTableLabels[params.target_table] || params.target_table)
    }
    if (params.entity_level) {
      parts.push(entityLevelLabels[params.entity_level] || params.entity_level)
    }
    if (params.reference_year) {
      parts.push(String(params.reference_year))
    }
    return parts.length > 0 ? parts.join(', ') : null
  }

  const parts: string[] = []

  // Edition info: "Edition 2025 erstellt" or "Edition 2025 aktualisiert"
  if (params.edition_name) {
    const modeText = params.edition_mode === 'update' ? 'aktualisiert' : 'erstellt'
    parts.push(`Edition ${params.edition_name} ${modeText}`)
  }

  // Reference year
  if (params.reference_year) {
    parts.push(`Referenzjahr ${params.reference_year}`)
  }

  // For rollback jobs
  if (params.original_job_id) {
    parts.push(`Original: ${params.original_job_id.slice(0, 8)}...`)
  }

  return parts.length > 0 ? parts.join('  •  ') : null
}

function StatusBadge({ status }: { status: string }) {
  const config = statusConfig[status] || { color: 'bg-gray-100 text-gray-700', icon: null, label: status }
  return (
    <span className={cn('inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium', config.color)}>
      {config.icon}
      {config.label}
    </span>
  )
}

function ProgressBar({ progress }: { progress: JobProgress }) {
  return (
    <div className="w-full">
      <div className="flex justify-between text-xs text-gray-500 mb-1">
        <span>{progress.message || 'In Bearbeitung...'}</span>
        <span>{Math.round(progress.percentage)}%</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div
          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
          style={{ width: `${progress.percentage}%` }}
        />
      </div>
      <div className="text-xs text-gray-400 mt-1">
        {progress.current.toLocaleString('de-DE')} / {progress.total.toLocaleString('de-DE')}
      </div>
    </div>
  )
}

function JobRow({ job, onCancel, onRollback }: { job: Job; onCancel: (id: string) => void; onRollback: (id: string) => void }) {
  const [expanded, setExpanded] = useState(false)
  const isActive = job.status === 'running' || job.status === 'queued'
  const canCancel = job.status === 'pending' || job.status === 'queued' || job.status === 'running'
  const subtitle = getJobSubtitle(job)

  return (
    <div className="bg-white border border-gray-200 rounded-lg mb-2">
      <div
        className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-4 flex-1 min-w-0">
          <div className="flex-shrink-0">
            <StatusBadge status={job.status} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-medium text-gray-900 truncate">
              {jobTypeLabels[job.job_type] || job.job_type}
              {subtitle && (
                <span className="ml-2 text-sm font-normal text-gray-500">
                  {subtitle}
                </span>
              )}
            </div>
            <div className="text-xs text-gray-500 truncate">
              ID: {job.job_id}
            </div>
          </div>
          {isActive && (
            <div className="hidden md:block w-48">
              <ProgressBar progress={job.progress} />
            </div>
          )}
          <div className="text-sm text-gray-500 hidden lg:block">
            {job.created_at && new Date(job.created_at).toLocaleString('de-DE', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </div>
        </div>
        <div className="flex items-center gap-2 ml-4">
          {canCancel && (
            <button
              onClick={(e) => { e.stopPropagation(); onCancel(job.job_id); }}
              className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
              title="Abbrechen"
            >
              <XCircle className="w-4 h-4" />
            </button>
          )}
          {job.can_rollback && job.status === 'completed' && (
            <button
              onClick={(e) => { e.stopPropagation(); onRollback(job.job_id); }}
              className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded transition-colors"
              title="Rückgängig machen"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
          )}
          {expanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </div>
      </div>

      {expanded && (
        <div className="px-4 pb-4 border-t border-gray-100 pt-4 space-y-4">
          {isActive && (
            <div className="md:hidden">
              <ProgressBar progress={job.progress} />
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Erstellt:</span>
              <div className="font-medium">
                {job.created_at ? new Date(job.created_at).toLocaleString('de-DE') : '-'}
              </div>
            </div>
            <div>
              <span className="text-gray-500">Gestartet:</span>
              <div className="font-medium">
                {job.started_at ? new Date(job.started_at).toLocaleString('de-DE') : '-'}
              </div>
            </div>
            <div>
              <span className="text-gray-500">Abgeschlossen:</span>
              <div className="font-medium">
                {job.completed_at ? new Date(job.completed_at).toLocaleString('de-DE') : '-'}
              </div>
            </div>
            <div>
              <span className="text-gray-500">Dauer:</span>
              <div className="font-medium">
                {job.duration_seconds
                  ? job.duration_seconds < 60
                    ? `${Math.round(job.duration_seconds)}s`
                    : `${Math.floor(job.duration_seconds / 60)}m ${Math.round(job.duration_seconds % 60)}s`
                  : '-'}
              </div>
            </div>
          </div>

          {job.error_message && (
            <div className="bg-red-50 border border-red-200 rounded p-3">
              <div className="flex items-center gap-2 text-red-800 font-medium text-sm">
                <AlertCircle className="w-4 h-4" />
                Fehler
              </div>
              <div className="text-red-700 text-sm mt-1 whitespace-pre-wrap">
                {job.error_message}
              </div>
            </div>
          )}

          {job.result && Object.keys(job.result).length > 0 && (
            <div className="bg-gray-50 border border-gray-200 rounded p-3">
              <div className="text-sm font-medium text-gray-700 mb-2">Ergebnis</div>
              <pre className="text-xs text-gray-600 overflow-x-auto">
                {JSON.stringify(job.result, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export function JobsPage() {
  const [statusFilter, setStatusFilter] = useState<string>('')
  const queryClient = useQueryClient()

  // Fetch jobs
  const { data: jobsData, isLoading, error, refetch } = useQuery<JobListResponse>({
    queryKey: ['jobs', statusFilter],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (statusFilter) params.set('status', statusFilter)
      params.set('limit', '50')
      const res = await fetch(`${API_BASE}/admin/jobs?${params}`)
      if (!res.ok) throw new Error('Failed to fetch jobs')
      return res.json()
    },
    refetchInterval: 5000, // Refresh every 5 seconds for active jobs
  })

  // Fetch stats
  const { data: stats } = useQuery<JobStats>({
    queryKey: ['job-stats'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/admin/jobs/stats/summary`)
      if (!res.ok) throw new Error('Failed to fetch stats')
      return res.json()
    },
    refetchInterval: 10000,
  })

  // Cancel mutation
  const cancelMutation = useMutation({
    mutationFn: async (jobId: string) => {
      const res = await fetch(`${API_BASE}/admin/jobs/${jobId}/cancel`, { method: 'POST' })
      if (!res.ok) throw new Error('Failed to cancel job')
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] })
      queryClient.invalidateQueries({ queryKey: ['job-stats'] })
    },
  })

  // Rollback mutation
  const rollbackMutation = useMutation({
    mutationFn: async (jobId: string) => {
      const res = await fetch(`${API_BASE}/admin/jobs/${jobId}/rollback`, { method: 'POST' })
      if (!res.ok) throw new Error('Failed to rollback job')
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] })
      queryClient.invalidateQueries({ queryKey: ['job-stats'] })
    },
  })

  const handleCancel = (jobId: string) => {
    if (confirm('Job wirklich abbrechen?')) {
      cancelMutation.mutate(jobId)
    }
  }

  const handleRollback = (jobId: string) => {
    if (confirm('Import wirklich rückgängig machen? Alle importierten Daten werden gelöscht.')) {
      rollbackMutation.mutate(jobId)
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Jobs</h1>
          <p className="text-gray-500 mt-1">Hintergrund-Jobs verwalten</p>
        </div>
        <button
          onClick={() => refetch()}
          className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Aktualisieren
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          {Object.entries(statusConfig).map(([status, config]) => (
            <button
              key={status}
              onClick={() => setStatusFilter(statusFilter === status ? '' : status)}
              className={cn(
                'p-4 rounded-lg border text-left transition-all',
                statusFilter === status
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              )}
            >
              <div className="flex items-center gap-2 text-sm text-gray-500">
                {config.icon}
                {config.label}
              </div>
              <div className="text-2xl font-bold mt-1">
                {stats.status_counts[status] || 0}
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Filter info */}
      {statusFilter && (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <span>Filter:</span>
          <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded">
            {statusConfig[statusFilter]?.label || statusFilter}
          </span>
          <button
            onClick={() => setStatusFilter('')}
            className="text-blue-600 hover:underline"
          >
            Zurücksetzen
          </button>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-red-800">
            <AlertCircle className="w-5 h-5" />
            <span>Fehler beim Laden der Jobs</span>
          </div>
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center h-32">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      )}

      {/* Job List */}
      {jobsData && (
        <div>
          <div className="text-sm text-gray-500 mb-4">
            {jobsData.total} Jobs gefunden
          </div>
          {jobsData.jobs.length === 0 ? (
            <div className="bg-white border border-gray-200 rounded-lg p-8 text-center text-gray-500">
              Keine Jobs gefunden
            </div>
          ) : (
            <div>
              {jobsData.jobs.map((job) => (
                <JobRow
                  key={job.job_id}
                  job={job}
                  onCancel={handleCancel}
                  onRollback={handleRollback}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
